import { Box } from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ConfirmDialog from '../Dialog/ConfirmationDialog';
import EditableSectionHeader from '../EditableSectionHeader';

interface EditableSectionProps {
	title: string;
	onSaveChanges: (utils: { toggleEditMode: () => void }) => void;
	children: (params: { isEditMode: boolean }) => React.ReactNode;
	dataTestId?: string;
	// Always controlled. The react-hook-form host must own this state: react-hook-form 7
	// drops the values of unmounted fields only when the `useForm` component itself
	// re-renders, so a section that toggled edit mode on its own would keep a
	// discarded draft (and its errors) alive for the next edit.
	isEditMode: boolean;
	onEditModeChange: (isEditMode: boolean) => void;
	/** HTML element for the section title; the `h6` look is unchanged (design.md §2). Defaults to `h6`. */
	titleComponent?: React.ElementType;
}

const EditableSection = ({
	title,
	onSaveChanges,
	children,
	dataTestId,
	isEditMode,
	onEditModeChange,
	titleComponent,
}: EditableSectionProps) => {
	const { t } = useTranslation();
	const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState<boolean>(false);

	const toggleConfirmDialog = () => setIsConfirmDialogOpen((prev) => !prev);
	const toggleEditMode = () => onEditModeChange(!isEditMode);

	const confirmDiscardAndClose = () => {
		toggleEditMode();
		toggleConfirmDialog();
	};

	const onClickSave = () => {
		onSaveChanges({ toggleEditMode });
	};

	return (
		<Box data-testid={dataTestId}>
			<EditableSectionHeader
				title={title}
				titleComponent={titleComponent}
				isEditMode={isEditMode}
				onEdit={toggleEditMode}
				toggleConfirmDialog={toggleConfirmDialog}
				onSave={onClickSave}
			/>
			{children({ isEditMode })}
			<ConfirmDialog
				isOpen={isConfirmDialogOpen}
				title={t('Quit editing without saving')}
				message={t('Changes you made may not be saved. Are you sure you want to cancel your changes?')}
				onConfirm={confirmDiscardAndClose}
				onCloseModal={toggleConfirmDialog}
				confirmButtonText={t('Discard')}
				confirmButtonColor="error"
			/>
		</Box>
	);
};

export default EditableSection;
